<script setup>
const props = defineProps({
  idPrefix: { type: String, default: "reference" },
  label: { type: String, default: "Reference sections" },
  tabs: { type: Array, required: true },
  activeTab: { type: String, required: true },
});
const emit = defineEmits(["select"]);
const onKeydown = (event) => {
  const index = props.tabs.findIndex((tab) => tab.id === props.activeTab);
  let next;
  if (event.key === "ArrowRight") next = (index + 1) % props.tabs.length;
  if (event.key === "ArrowLeft")
    next = (index - 1 + props.tabs.length) % props.tabs.length;
  if (event.key === "Home") next = 0;
  if (event.key === "End") next = props.tabs.length - 1;
  if (next === undefined) return;
  event.preventDefault();
  event.currentTarget.querySelectorAll('[role="tab"]')[next]?.focus();
  emit("select", props.tabs[next].id);
};
</script>

<template>
  <div
    class="reference-tab-navigation sticky top-0 z-20 rounded-xl border border-slate-200 bg-white p-2 shadow-sm dark:border-slate-700 dark:bg-slate-900"
  >
    <div class="reference-mobile-tabs">
      <label
        :for="`${idPrefix}-section`"
        class="mb-1 block px-1 text-xs font-semibold text-slate-500 dark:text-slate-400"
        >{{ label }}</label
      >
      <select
        :id="`${idPrefix}-section`"
        :value="activeTab"
        class="w-full rounded-lg border border-slate-300 bg-white px-3 py-3 text-sm font-semibold text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
        @change="emit('select', $event.target.value)"
      >
        <option v-for="tab in tabs" :key="tab.id" :value="tab.id">
          {{ tab.label }}{{ tab.notice ? ` — ${tab.notice}` : "" }}
        </option>
      </select>
    </div>
    <div
      class="reference-desktop-tabs gap-1"
      role="tablist"
      :aria-label="label"
      @keydown="onKeydown"
    >
      <button
        v-for="tab in tabs"
        :id="`${idPrefix}-tab-${tab.id}`"
        :key="tab.id"
        type="button"
        role="tab"
        :aria-selected="activeTab === tab.id"
        :aria-controls="`${idPrefix}-tab-panel`"
        :tabindex="activeTab === tab.id ? 0 : -1"
        class="min-h-11 rounded-lg px-4 py-3 text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
        :class="
          activeTab === tab.id
            ? 'bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-100'
            : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
        "
        @click="emit('select', tab.id)"
      >
        {{ tab.label }}
        <span v-if="tab.notice" class="ml-1 text-xs">· {{ tab.notice }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.reference-desktop-tabs {
  display: none;
}
@container reference-workspace (min-width: 40rem) {
  .reference-mobile-tabs {
    display: none;
  }
  .reference-desktop-tabs {
    display: flex;
    flex-wrap: wrap;
  }
}
</style>
